/**
 * Lockbox domain model — pure data shapes (JSDoc for IDE support).
 * No React, no native, no network.
 *
 * ## Product invariant (Apple platforms)
 * This layer **never** “blocks” at the OS level. It only computes **restriction intent**.
 * FamilyControls / ManagedSettings / DeviceActivity are the system enforcer; the user must
 * approve; timing and coverage follow Apple’s rules—not a firewall guarantee.
 *
 * **Three layers:** (1) JS policy here — (2) enforceable ids/refs in `EnforcementPlan` — (3) Swift maps to
 * `ApplicationToken` only; **never** send tokens to JS; **never** use `bundle_hint` for `shield.applications`.
 */

/** @typedef {'navigation'|'banking'|'productivity'|'learning'|'utility'|'work_comms'|'anime_streaming'|'manga_reading'|'shortform_feed'|'gaming_session'|'entertainment_discovery'|'unknown'} BehavioralCategory */

/** @typedef {'none'|'sexualized_content_risk'|'compulsive_scroll'|'novelty_feed'|'escapism_media'|'high_frequency_notifications'|'unknown'} TriggerType */

/** @typedef {'clean'|'flagged'|'unknown'} ContentSeverity */

/**
 * @typedef {Object} ClassificationMeta
 * @property {'heuristic'|'play_store'|'ai'|'merged'} [source]
 * @property {string} [classifiedAt] ISO timestamp
 * @property {string[]} [matchedPatterns]
 * @property {string[]} [severityHits]
 * @property {string} [playStoreRating]
 * @property {string} [aiReason]
 */

/**
 * @typedef {Object} AppScores
 * @property {number} risk 0–100
 * @property {number} productivity 0–100
 * @property {number} distraction 0–100
 * @property {number} confidence 0–1
 */

/**
 * @typedef {Object} OverrideState
 * @property {'none'|'force_allow'|'force_block'} kind
 * @property {string} [reason]
 * @property {string} [untilIso]
 */

/**
 * @typedef {Object} AppClassification
 * @property {BehavioralCategory} primaryCategory
 * @property {BehavioralCategory[]} secondaryCategories
 * @property {TriggerType[]} triggerTypes
 * @property {AppScores} scores
 * @property {OverrideState} override
 * @property {ContentSeverity} [contentSeverity] disturbing / explicit content signals
 * @property {ClassificationMeta} [classificationMeta] how category/severity were derived
 * @property {boolean} [userFlaggedSeverity] user tapped "Block — disturbing content"
 * @property {boolean} [severityAutoBlocked] auto-blocked globally due to flagged severity
 */

/**
 * @typedef {Object} NativeAppRef
 * @property {'family_controls_pending'|'opaque_token'} kind
 * @property {string} [opaqueKey] **applicationTokenRef** — opaque string from Swift after picker; never the serialized `ApplicationToken`
 */

/**
 * One row in the lockbox inventory (user-visible app / slot).
 * @typedef {Object} LockboxAppEntity
 * @property {string} id stable UUID
 * @property {string} displayName
 * @property {string} [bundleId] reverse-DNS **hint** for UX / iOS simulator — not a ManagedSettings input; pair with picker → store ref in `nativeRef`
 * @property {string} [packageName] Android `packageName` for enforcement (`android_package` target)
 * @property {AppClassification} classification
 * @property {NativeAppRef|null} nativeRef opaque **applicationTokenRef** handle (never the raw ApplicationToken)
 */

/**
 * @typedef {Object} TimeWindowRule
 * @property {string} id
 * @property {string} name
 * @property {number} startHour 0–23
 * @property {number} startMinute 0–59
 * @property {number} endHour 0–23
 * @property {number} endMinute 0–59
 * @property {BehavioralCategory[]} blockCategories during window
 * @property {string[]} [onlyInModes] if empty, applies in all modes
 */

/**
 * @typedef {Object} ModeStrictRule
 * @property {string} modeId
 * @property {BehavioralCategory[]} [blockedCategories] genres this mode suggests blocking (template)
 * @property {BehavioralCategory[]} extraBlockCategories categories actively enforced when mode is on
 */

/**
 * @typedef {Object} EmergencyPolicy
 * @property {'pause_automatic_blocks'|'allow_all_except_force_block'} strategy
 */

/**
 * Versioned rulepack — evaluated entirely in JS.
 * @typedef {Object} LockboxRulepack
 * @property {number} version
 * @property {BehavioralCategory[]} alwaysAllowCategories
 * @property {BehavioralCategory[]} alwaysBlockCategories
 * @property {TimeWindowRule[]} timeWindows
 * @property {ModeStrictRule[]} modeStrict
 * @property {EmergencyPolicy} emergencyPolicy
 */

/**
 * @typedef {Object} EvaluationWorld
 * @property {Date} now
 * @property {string} activeModeId
 * @property {{ active: boolean, until: Date|null }} emergency
 * @property {LockboxAppEntity[]} apps
 */

/**
 * Declared outcome of policy evaluation — **intent for the system bridge**, not a hard guarantee.
 * Prefer thinking of this type as `RestrictionIntent` (alias name below).
 *
 * ## Identity vs enforcement (iOS)
 * - `restrictTargets` / `allowTargets`: **enforceable only** — `lockbox_app` + `application_token_ref`.
 *   Swift resolves these to `ApplicationToken` (never sent to JS).
 * - `restrictBundleHints` / `allowBundleHints`: **not enforceable** — `bundle_hint` for simulator / UX / catalog.
 *   Must **not** be passed to `ManagedSettingsStore.shield.applications`.
 *
 * @typedef {Object} EnforcementPlan
 * @property {number} version
 * @property {string} computedAt ISO
 * @property {string} activeModeId
 * @property {string[]} blockAppIds restrict intent (internal Lockbox app ids)
 * @property {string[]} allowAppIds allow intent (internal Lockbox app ids)
 * @property {import('./restrictionTargets.js').RestrictionTarget[]} restrictTargets enforceable restrict intent only
 * @property {import('./restrictionTargets.js').RestrictionTarget[]} allowTargets enforceable allow intent only
 * @property {import('./restrictionTargets.js').RestrictionTarget[]} restrictBundleHints bundle_hint only (blocked rows)
 * @property {import('./restrictionTargets.js').RestrictionTarget[]} allowBundleHints bundle_hint only (allowed rows)
 * @property {{ id: string, label: string, payload: Record<string, unknown> }[]} scheduleHints hints for DeviceActivity (system applies)
 * @property {{ ruleId: string, reason: string, appId?: string }[]} matchedRules explainability only
 * @property {'monitor'|'soft'|'strict'} [enforcementMode] native execution hint (Android); default strict when bridged
 * @property {string} [planId] stable id for native revision tracking
 * @property {number} [revision] monotonic apply counter
 * @property {Record<string, string>} [androidBlockReasons] packageName → human reason for overlay (Android)
 */

/**
 * @typedef {EnforcementPlan} RestrictionIntent
 */

export const BEHAVIORAL_CATEGORIES = /** @type {const} */ ([
  "navigation",
  "banking",
  "productivity",
  "learning",
  "utility",
  "work_comms",
  "anime_streaming",
  "manga_reading",
  "shortform_feed",
  "gaming_session",
  "entertainment_discovery",
  "unknown",
]);

export const TRIGGER_TYPES = /** @type {const} */ ([
  "none",
  "sexualized_content_risk",
  "compulsive_scroll",
  "novelty_feed",
  "escapism_media",
  "high_frequency_notifications",
  "unknown",
]);

/** @returns {AppScores} */
export function defaultScores() {
  return { risk: 0, productivity: 50, distraction: 50, confidence: 0.2 };
}

/** @returns {OverrideState} */
export function defaultOverride() {
  return { kind: "none" };
}

/** @returns {AppClassification} */
export function defaultClassification(primaryCategory = "unknown") {
  return {
    primaryCategory,
    secondaryCategories: [],
    triggerTypes: ["none"],
    scores: defaultScores(),
    override: defaultOverride(),
  };
}
